// src/pages/admin/bookings/BookingDetailsPage.tsx

import * as React from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
    Snackbar,
    Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import BookingStatusChip from "./components/BookingStatusChip";
import BookingDates from "./components/BookingDates";
import BookingCustomerCard from "./components/BookingCustomerCard";
import { BookingDetails, DEFAULT_BOOKING_STATUSES, safeStr } from "./bookingTypes";
import { adminFetch } from "../../../services/adminFetch";

export default function AdminBookingDetailsPage() {
    const { locale, id } = useParams<{ locale: string; id: string }>();
    const navigate = useNavigate();

    const bookingId = Number(id);
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [err, setErr] = React.useState<string | null>(null);

    const [item, setItem] = React.useState<BookingDetails | null>(null);

    const [status, setStatus] = React.useState<string>("");
    const [internalNotes, setInternalNotes] = React.useState<string>("");

    const [toast, setToast] = React.useState<{
        open: boolean;
        msg: string;
        severity: "success" | "error";
    }>({
        open: false,
        msg: "",
        severity: "success",
    });

    const load = React.useCallback(async () => {
        if (!Number.isFinite(bookingId)) {
            setErr("Invalid booking id.");
            return;
        }
        setLoading(true);
        setErr(null);
        try {
            const res = await adminFetch(`/api/admin/bookings/${bookingId}`, { method: "GET" });
            const j = (await res.json().catch(() => ({}))) as any;
            if (!res.ok) throw new Error(j?.error || `Request failed (${res.status})`);

            const b = (j?.item ?? j?.booking ?? j) as BookingDetails;

            setItem(b || null);
            setStatus(safeStr(b?.status, ""));
            setInternalNotes(safeStr(b?.internal_notes, ""));
        } catch (e: any) {
            setErr(e?.message || "Failed to load booking.");
            setItem(null);
        } finally {
            setLoading(false);
        }
    }, [bookingId]);

    React.useEffect(() => {
        load();
    }, [load]);

    const onSave = async () => {
        if (!item) return;
        setSaving(true);
        try {
            const res = await adminFetch(`/api/admin/bookings/${bookingId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    status: status || null,
                    internal_notes: internalNotes || null,
                }),
            });
            const j = (await res.json().catch(() => ({}))) as any;
            if (!res.ok) throw new Error(j?.error || `Save failed (${res.status})`);

            // Prefer updated item from server; fallback keep local
            const updated = (j?.item ?? j?.booking ?? j) as BookingDetails;
            const next = updated || { ...item, status, internal_notes: internalNotes };
            setItem(next);

            setToast({ open: true, msg: "Saved successfully.", severity: "success" });
        } catch (e: any) {
            setToast({ open: true, msg: e?.message || "Save failed.", severity: "error" });
        } finally {
            setSaving(false);
        }
    };

    const slug = item?.property?.slug ?? item?.property_slug ?? null;
    const propertyTitle =
        item?.property?.title ??
        item?.property_title ??
        (item?.property_id ? `Property #${item.property_id}` : "—");

    const createdLabel = item?.created_at ? `Created: ${item.created_at}` : null;
    const updatedLabel = item?.updated_at ? `Updated: ${item.updated_at}` : null;

    return (
        <Stack spacing={2}>
            <Paper
                elevation={0}
                variant="outlined"
                sx={{
                    borderRadius: 3,
                    borderColor: "divider",
                    overflow: "hidden",
                    background:
                        "radial-gradient(900px 500px at 15% -20%, rgba(156,39,176,0.10), transparent 60%), radial-gradient(900px 500px at 85% -10%, rgba(25,118,210,0.10), transparent 60%)",
                }}
            >
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    alignItems={{ md: "center" }}
                    sx={{ p: 2, gap: 1.5 }}
                >
                    <Stack spacing={0.25}>
                        <Typography variant="h6" sx={{ fontWeight: 950 }}>
                            Booking #{Number.isFinite(bookingId) ? bookingId : "—"}
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2" color="text.secondary">
                                {propertyTitle}
                            </Typography>

                            <BookingStatusChip status={item?.status ?? null} />

                            {createdLabel ? (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={createdLabel}
                                    sx={{ borderRadius: 999, fontWeight: 800 }}
                                />
                            ) : null}

                            {updatedLabel ? (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={updatedLabel}
                                    sx={{ borderRadius: 999, fontWeight: 800 }}
                                />
                            ) : null}
                        </Stack>
                    </Stack>

                    <Box sx={{ flex: 1 }} />

                    <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        justifyContent={{ xs: "flex-start", md: "flex-end" }}
                    >
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(-1)}
                            sx={{ borderRadius: 2, fontWeight: 900 }}
                        >
                            Back
                        </Button>

                        {slug ? (
                            <Button
                                component={RouterLink}
                                to={`/${locale || "el"}/properties/${slug}`}
                                variant="outlined"
                                startIcon={<OpenInNewIcon />}
                                sx={{ borderRadius: 2, fontWeight: 900 }}
                            >
                                View property
                            </Button>
                        ) : null}

                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                            disabled={saving || loading || !item}
                            onClick={onSave}
                            sx={{ borderRadius: 2, fontWeight: 950 }}
                        >
                            Save
                        </Button>
                    </Stack>
                </Stack>

                <Divider />

                <Box sx={{ p: 2 }}>
                    {err ? <Alert severity="error">{err}</Alert> : null}

                    {loading ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                            <CircularProgress size={18} />
                            <Typography variant="body2" color="text.secondary">
                                Loading…
                            </Typography>
                        </Stack>
                    ) : item ? (
                        <Stack spacing={2}>
                            {/* Stay details */}
                            <Paper variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                                <Stack spacing={1}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                                        Stay details
                                    </Typography>
                                    <Divider />

                                    <Stack
                                        direction={{ xs: "column", md: "row" }}
                                        spacing={2}
                                        alignItems={{ md: "center" }}
                                    >
                                        <Stack spacing={0.5} sx={{ flex: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Dates
                                            </Typography>
                                            <BookingDates checkIn={item.check_in} checkOut={item.check_out} />
                                        </Stack>

                                        <Stack spacing={0.5} sx={{ minWidth: 160 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Guests
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 900 }}>
                                                {item.guests_total ?? "—"}
                                            </Typography>
                                        </Stack>

                                        <Stack spacing={0.5} sx={{ minWidth: 240 }}>
                                            <TextField
                                                select
                                                label="Status"
                                                value={status}
                                                onChange={(e) => setStatus(e.target.value)}
                                                size="small"
                                                fullWidth
                                            >
                                                {DEFAULT_BOOKING_STATUSES.filter((x) => x.value !== "").map((o) => (
                                                    <MenuItem key={o.value} value={o.value}>
                                                        {o.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </Paper>

                            {/* Customer + Notes */}
                            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
                                <Box sx={{ flex: 1 }}>
                                    <BookingCustomerCard
                                        customer={item.customer ?? null}
                                        fallbackFullName={null}
                                        fallbackEmail={null}
                                        fallbackPhone={null}
                                    />
                                </Box>

                                <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, flex: 1 }}>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                                            Internal notes
                                        </Typography>
                                        <Divider />
                                        <TextField
                                            value={internalNotes}
                                            onChange={(e) => setInternalNotes(e.target.value)}
                                            multiline
                                            minRows={6}
                                            placeholder="Add internal notes for this booking…"
                                            fullWidth
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            Only admins can see these notes.
                                        </Typography>
                                    </Stack>
                                </Paper>
                            </Stack>

                            {/* Raw fields */}
                            <Paper variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                                <Stack spacing={1}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
                                        Raw fields
                                    </Typography>
                                    <Divider />

                                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap">
                                        <Stack spacing={0.25} sx={{ minWidth: 220 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Property
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 850 }}>
                                                ID: {item.property_id ?? "—"}
                                            </Typography>
                                            {slug ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    Slug: {slug}
                                                </Typography>
                                            ) : null}
                                        </Stack>

                                        <Stack spacing={0.25} sx={{ minWidth: 220 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Customer
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 850 }}>
                                                ID: {item.customer_id ?? "—"}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Name: {item.customer?.full_name ?? "—"}
                                            </Typography>
                                        </Stack>

                                        <Stack spacing={0.25} sx={{ minWidth: 240 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Inquiry link
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 850 }}>
                                                Inquiry ID: {item.booking_inquiry_id ?? "—"}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Source: {safeStr((item as any)?.source, "—")}
                                            </Typography>
                                        </Stack>
                                    </Stack>

                                    <Divider />

                                    <Typography variant="body2" color="text.secondary">
                                        Status (current):{" "}
                                        <Box component="span" sx={{ fontWeight: 900, color: "text.primary" }}>
                                            {safeStr(item.status, "—")}
                                        </Box>
                                        {"  "}•{"  "}
                                        Check-in:{" "}
                                        <Box component="span" sx={{ fontWeight: 900, color: "text.primary" }}>
                                            {safeStr(item.check_in, "—")}
                                        </Box>
                                        {"  "}•{"  "}
                                        Check-out:{" "}
                                        <Box component="span" sx={{ fontWeight: 900, color: "text.primary" }}>
                                            {safeStr(item.check_out, "—")}
                                        </Box>
                                    </Typography>

                                    {item?.updated_at || item?.created_at ? (
                                        <Typography variant="body2" color="text.secondary">
                                            {item.created_at ? `Created at: ${item.created_at}` : ""}
                                            {item.created_at && item.updated_at ? " • " : ""}
                                            {item.updated_at ? `Updated at: ${item.updated_at}` : ""}
                                        </Typography>
                                    ) : null}
                                </Stack>
                            </Paper>
                        </Stack>
                    ) : (
                        <Alert severity="info">No booking found.</Alert>
                    )}
                </Box>
            </Paper>

            <Snackbar
                open={toast.open}
                autoHideDuration={3500}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={toast.severity}
                    variant="filled"
                    onClose={() => setToast((t) => ({ ...t, open: false }))}
                    sx={{ fontWeight: 800 }}
                >
                    {toast.msg}
                </Alert>
            </Snackbar>
        </Stack>
    );
}
